import { useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { Link, useNavigate, useParams } from "@tanstack/react-router";
import { Icon } from "@majourneys/icons";
import { SelectMenu, type MenuOption } from "@majourneys/ui";
import {
  CREATOR_PLATFORMS,
  SUPPORT_PHONE_DISPLAY,
  WHATSAPP_URL,
  creatorHandle,
  type CreatorPlatform,
} from "@majourneys/shared";
import { formatNumber } from "../../../lib/format";
import { useSession } from "../../../lib/auth";
import { PageSeo, SITE_ORIGIN } from "../../../shared/seo/page-seo";
import { panelStyle } from "../../../shared/ui/card-frame";
import { PLATFORM_ICONS, PLATFORM_LABEL_KEYS } from "../../creators/constants/creator-options";
import { useAmbassadorMe, useAmbassadorMutations } from "../hooks/use-ambassador";
import { AmbassadorBadge, AmbassadorLogo, AmbassadorShield } from "../components/ambassador-badge";
import { ApplyForm, profileUrlFor, type ApplyCarry } from "../components/apply-form";
import { SampleDashboard, SamplePage, SharePlaces, StickyApply } from "../components/join-extras";
import {
  ERROR_TEXT,
  Eyebrow,
  GOLD,
  GOLD_DARK,
  HAIRLINE,
  MUTED,
  NAVY,
  NAVY_GRADIENT,
  PRIMARY_SHADOW,
  Panel,
  SOFT_TINT,
  CONTROL_EDGE,
  prefersReducedMotion,
  primaryClass,
  primaryStyle,
  secondaryClass,
  secondaryStyle,
  shake,
  NAVY_FACE,
} from "../components/kit";

/**
 * Join page, /<lang>/ambassadors (owner-approved lab, 2026-10-09).
 *
 * Public and edge-cacheable: the HTML is the same for everyone. Whether the
 * visitor is already an ambassador or has applied is read in the browser
 * (`useAmbassadorMe`), so the apply panel swaps after hydration.
 *
 *   - Hero: same build as /creators. One pill (platform + @handle + Apply);
 *     Apply carries both into the form below and submits nothing.
 *   - The ambassador card on the right fills in live from the pill, then
 *     shows every platform picked in the form.
 *   - Six reasons, five steps, three FAQs, then the application.
 */

const BENEFITS = [
  { key: "earn", icon: "ri-wallet-3-line" },
  { key: "places", icon: "ri-sparkling-line" },
  { key: "page", icon: "ri-user-star-line" },
  { key: "stats", icon: "ri-cursor-line" },
  { key: "voice", icon: "ri-palette-line" },
  { key: "team", icon: "ri-team-line" },
] as const;

const STEPS = ["apply", "approved", "setup", "share", "paid"] as const;
const FAQS = ["when_paid", "how_long", "what_share"] as const;

/**
 * The platforms the live card shows: the ones picked in the form, or the
 * hero pill's while the form has none picked yet.
 */
export function cardPlatforms(picked: CreatorPlatform[], hero: CreatorPlatform): CreatorPlatform[] {
  return picked.length ? picked : [hero];
}

export function JoinPage() {
  const { t, i18n } = useTranslation();
  const params = useParams({ strict: false }) as { lang?: string };
  const lang = params.lang ?? i18n.language ?? "en";
  const isRtl = lang === "ar";
  const forward = isRtl ? "ri-arrow-left-line" : "ri-arrow-right-line";
  const navigate = useNavigate();
  const { data: session } = useSession();
  const me = useAmbassadorMe();
  const { apply } = useAmbassadorMutations();

  const [heroPlatform, setHeroPlatform] = useState<CreatorPlatform>("instagram");
  const [heroHandle, setHeroHandle] = useState("");
  const [heroError, setHeroError] = useState(false);
  const [carry, setCarry] = useState<ApplyCarry | null>(null);
  const [formPlatforms, setFormPlatforms] = useState<CreatorPlatform[]>([]);
  const shownPlatforms = cardPlatforms(formPlatforms, heroPlatform);
  const [sent, setSent] = useState(false);
  const heroPillRef = useRef<HTMLDivElement>(null);
  const applyRef = useRef<HTMLElement>(null);

  // Back from sign-in with `#apply`: land on the form, draft restored.
  useEffect(() => {
    if (typeof window !== "undefined" && window.location.hash === "#apply") {
      applyRef.current?.scrollIntoView({ block: "start" });
    }
  }, []);

  const menuLabels = (title: string) => ({
    title,
    listLabel: title,
    searchPlaceholder: t("common.menu_search"),
    clearSearch: t("common.menu_clear_search"),
    closeList: t("common.menu_close_list"),
    emptyTitle: t("common.menu_empty"),
    noMatchTitle: () => t("common.menu_no_match"),
    noMatchHint: t("common.menu_no_match_hint"),
  });

  const platformOptions = useMemo<MenuOption[]>(
    () =>
      CREATOR_PLATFORMS.map((p) => ({
        value: p,
        label: t(PLATFORM_LABEL_KEYS[p]),
        lead: <Icon name={PLATFORM_ICONS[p]} className="text-[16px]" aria-hidden />,
      })),
    [t],
  );

  const cleanHandle = creatorHandle(heroHandle)?.toLowerCase() ?? null;
  // The page address keeps only what a handle may hold.
  const pageHandle = (cleanHandle ?? "").replace(/[^a-z0-9_]/g, "").slice(0, 32);

  function onHeroApply() {
    const trimmed = heroHandle.trim();
    if (trimmed && !cleanHandle) {
      setHeroError(true);
      shake(heroPillRef.current);
      return;
    }
    setHeroError(false);
    setCarry({ platform: heroPlatform, profileUrl: profileUrlFor(heroPlatform, cleanHandle), nonce: Date.now() });
    applyRef.current?.scrollIntoView({ behavior: prefersReducedMotion() ? "auto" : "smooth", block: "start" });
  }

  /** Apply from further down the page (the phone bar, a made link): carries the hero's handle when it is valid. */
  function goToForm() {
    if (cleanHandle) setCarry({ platform: heroPlatform, profileUrl: profileUrlFor(heroPlatform, cleanHandle), nonce: Date.now() });
    applyRef.current?.scrollIntoView({ behavior: prefersReducedMotion() ? "auto" : "smooth", block: "start" });
  }

  function goSignIn() {
    // Same page, same query, back on the form (state preservation, item 6).
    const here =
      typeof window !== "undefined" ? `${window.location.pathname}${window.location.search}#apply` : `/${lang}/ambassadors#apply`;
    void navigate({ href: `/sign-in?redirect=${encodeURIComponent(here)}` });
  }

  const status = me.data?.ambassador ? "ambassador" : (me.data?.application?.status ?? null);

  const pageLd = {
    "@context": "https://schema.org",
    "@type": "WebPage",
    name: t("ambassadors.seo.title"),
    description: t("ambassadors.seo.description"),
    url: `${SITE_ORIGIN}/${lang}/ambassadors`,
    inLanguage: lang,
  };

  return (
    <div className="pb-20 pt-24" dir={isRtl ? "rtl" : "ltr"}>
      <PageSeo
        title={`${t("ambassadors.seo.title")} | MaJourneys`}
        description={t("ambassadors.seo.description")}
        canonicalPath={`/${lang}/ambassadors`}
        lang={lang}
        ogType="website"
        jsonLd={[pageLd]}
      />

      {/* 1 · Hero: one line, one pill, and the live card. */}
      <section className="px-4 pb-6 sm:px-5">
        <div className="mx-auto grid w-full max-w-[1120px] items-start gap-12 lg:grid-cols-[minmax(0,1fr)_360px]">
          <div className="flex min-w-0 flex-col items-start pt-4 lg:pt-12">
            <Eyebrow>{t("ambassadors.join.eyebrow")}</Eyebrow>
            <h1
              className="mt-3 font-sans text-[36px] font-semibold sm:text-[54px]"
              style={{ color: NAVY, letterSpacing: "-0.034em", lineHeight: 1.05 }}
            >
              {t("ambassadors.join.hero_title")}
            </h1>
            <p className="mt-4 max-w-[50ch] font-sans text-[15px] sm:text-[16.5px]" style={{ color: MUTED, lineHeight: 1.55 }}>
              {t("ambassadors.join.hero_subtitle")}
            </p>

            <div
              ref={heroPillRef}
              className="mt-8 flex w-full max-w-[560px] items-center gap-1.5 p-1.5 ps-2"
              style={panelStyle(999, "#FFFFFF")}
            >
              <SelectMenu
                name="ambassador-hero-platform"
                side="start"
                options={platformOptions}
                value={heroPlatform}
                onChange={(next) => setHeroPlatform(next as CreatorPlatform)}
                labels={menuLabels(t("ambassadors.form.platform"))}
                renderTrigger={({ ref, open, buttonProps }) => (
                  <button
                    {...buttonProps}
                    ref={ref}
                    aria-label={t("ambassadors.form.platform")}
                    className="mj-press flex h-11 shrink-0 cursor-pointer items-center gap-1.5 rounded-full px-3 font-sans text-[14px] font-semibold"
                    style={{ color: NAVY, background: open ? "rgba(17,14,57,0.05)" : undefined }}
                  >
                    <Icon name={PLATFORM_ICONS[heroPlatform]} className="text-[17px]" aria-hidden />
                    <span className="hidden sm:inline">{t(PLATFORM_LABEL_KEYS[heroPlatform])}</span>
                    <Icon
                      name="ri-arrow-down-s-line"
                      className="text-[16px] transition-transform duration-200 motion-reduce:transition-none"
                      style={{ color: MUTED, transform: open ? "rotate(180deg)" : undefined }}
                      aria-hidden
                    />
                  </button>
                )}
              />
              <span aria-hidden className="h-6 w-px shrink-0" style={{ background: "rgba(17,14,57,0.10)" }} />
              <input
                value={heroHandle}
                onChange={(e) => {
                  setHeroHandle(e.target.value);
                  if (heroError) setHeroError(false);
                }}
                onKeyDown={(e) => {
                  if (e.key === "Enter") {
                    e.preventDefault();
                    onHeroApply();
                  }
                }}
                dir="ltr"
                autoComplete="off"
                autoCapitalize="none"
                spellCheck={false}
                aria-label={t("ambassadors.join.hero_handle_label")}
                aria-invalid={heroError ? true : undefined}
                aria-describedby={heroError ? "amb-hero-handle-error" : undefined}
                placeholder={t("ambassadors.join.hero_handle_placeholder")}
                className="h-11 min-w-0 flex-1 bg-transparent px-2 font-sans text-[15px] text-[#110E39] outline-none placeholder:text-[#5a5d77]"
              />
              <button
                type="button"
                onClick={onHeroApply}
                className="mj-press inline-flex h-11 shrink-0 items-center gap-1.5 rounded-full px-5 font-sans text-[14.5px] font-semibold text-white"
                style={{ background: NAVY_FACE, boxShadow: PRIMARY_SHADOW }}
              >
                {t("ambassadors.join.hero_cta")}
                <Icon name={forward} className="text-[16px]" aria-hidden />
              </button>
            </div>
            {heroError ? (
              <p id="amb-hero-handle-error" className={`mj-fade-in mt-2.5 ${ERROR_TEXT}`}>
                {t("ambassadors.join.hero_handle_error")}
              </p>
            ) : (
              <>
                <p className="mt-3 font-sans text-[12.5px] font-semibold" style={{ color: GOLD_DARK }}>
                  {t("ambassadors.join.hero_note")}
                </p>
                {/* Said here, not first at the form (owner, 2026-10-10, suggestion 88). */}
                {!session?.user && (
                  <p className="mt-1.5 flex items-center gap-1.5 font-sans text-[12.5px]" style={{ color: MUTED }} data-testid="account-note">
                    <Icon name="ri-lock-line" className="text-[13px]" aria-hidden />
                    {t("ambassadors.join.account_note")}
                  </p>
                )}
              </>
            )}
          </div>

          {/* The ambassador card: fills in live from the pill. */}
          <aside className="min-w-0 lg:sticky lg:top-28" aria-label={t("ambassadors.join.card_title")}>
            <div className="p-6" style={panelStyle(28)}>
              <Eyebrow color={MUTED}>{t("ambassadors.join.card_title")}</Eyebrow>
              <div className="mt-4 flex min-w-0 items-center gap-3">
                <span
                  aria-hidden
                  className="grid h-14 w-14 shrink-0 place-items-center rounded-full font-sans text-white"
                  style={{ background: NAVY_GRADIENT }}
                >
                  {cleanHandle ? (
                    <span key={cleanHandle[0]} className="mj-pop-in text-[21px] font-semibold">
                      {cleanHandle[0]!.toUpperCase()}
                    </span>
                  ) : (
                    <Icon name={PLATFORM_ICONS[shownPlatforms[0]!]} className="text-[22px]" />
                  )}
                </span>
                <div className="min-w-0">
                  {/* clamp-ok: guest's own handle */}
                  <p
                    className="truncate font-sans text-[18px] font-semibold"
                    style={{ color: cleanHandle ? NAVY : MUTED }}
                    dir="ltr"
                  >
                    @{cleanHandle || t("ambassadors.join.card_handle_empty")}
                  </p>
                  <ul
                    className="mt-0.5 flex flex-wrap items-center gap-x-3 gap-y-1 font-sans text-[12.5px]"
                    style={{ color: MUTED }}
                    data-testid="ambassador-card-platforms"
                  >
                    {shownPlatforms.map((p) => (
                      <li key={p} className="mj-fade-in flex items-center gap-1.5">
                        <Icon name={PLATFORM_ICONS[p]} className="text-[13px]" aria-hidden />
                        {t(PLATFORM_LABEL_KEYS[p])}
                      </li>
                    ))}
                  </ul>
                </div>
              </div>

              <div className="mt-5 pt-4" style={{ borderTop: `1px solid ${HAIRLINE}` }}>
                <p className="font-sans text-[12.5px] font-semibold" style={{ color: NAVY }}>
                  {t("ambassadors.join.card_page")}
                </p>
                {/* clamp-ok: page URL with typed handle */}
                <p className="mt-1 truncate font-sans text-[13.5px]" style={{ color: MUTED }} dir="ltr">
                  majourneys.com/a/
                  <span style={{ color: NAVY, fontWeight: 600 }}>
                    {pageHandle || t("ambassadors.join.card_handle_empty")}
                  </span>
                </p>
              </div>

              <div className="mt-5 flex items-center gap-2">
                <AmbassadorShield size={26} />
                <AmbassadorBadge small />
              </div>
            </div>
          </aside>
        </div>
      </section>

      {/* 2 · Why ambassadors join. */}
      <section className="mt-10 py-16" style={{ background: "linear-gradient(180deg,#f7f7fb 0%,#fff 100%)" }}>
        <div className="mx-auto max-w-[1180px] px-5">
          <h2
            className="text-center font-sans text-[26px] font-semibold sm:text-[32px]"
            style={{ color: NAVY, letterSpacing: "-0.03em" }}
          >
            {t("ambassadors.join.benefits_title")}
          </h2>
          <ul className="mt-10 grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
            {BENEFITS.map((b) => (
              <li key={b.key} className="flex min-w-0">
                <Panel className="w-full p-6" radius={24}>
                  <span
                    aria-hidden
                    className="grid h-11 w-11 place-items-center rounded-[14px]"
                    style={{ background: NAVY_GRADIENT, boxShadow: "0 10px 24px -12px rgba(17,14,57,0.8), inset 0 1px 0 rgba(255,255,255,0.16)", color: GOLD }}
                  >
                    <Icon name={b.icon} className="text-[20px]" />
                  </span>
                  <h3 className="mt-4 font-sans text-[16.5px] font-semibold" style={{ color: NAVY }}>
                    {t(`ambassadors.join.benefit_${b.key}_title`)}
                  </h3>
                  <p className="mt-1.5 font-sans text-[14.5px] leading-relaxed" style={{ color: MUTED }}>
                    {t(`ambassadors.join.benefit_${b.key}_body`)}
                  </p>
                </Panel>
              </li>
            ))}
          </ul>
        </div>
      </section>

      {/* Real places to share, and a look at the dashboard (suggestions 86, 87). */}
      <SharePlaces lang={lang} handle={pageHandle} onApply={goToForm} />
      <SampleDashboard lang={lang} />
      <SamplePage lang={lang} handle={pageHandle} />

      {/* 3 · How it works, and the three questions everyone asks. */}
      <section className="mx-auto max-w-[1180px] px-5 py-16">
        <h2
          className="text-center font-sans text-[26px] font-semibold sm:text-[32px]"
          style={{ color: NAVY, letterSpacing: "-0.03em" }}
        >
          {t("ambassadors.join.steps_title")}
        </h2>
        {/* On a phone one timeline, on wider screens five cards (owner, 2026-10-10, suggestion 89). */}
        <ol className="mt-8 flex flex-col md:mt-10 md:grid md:grid-cols-5 md:gap-4" data-testid="join-steps">
          {STEPS.map((s, i) => (
            <li key={s} className="relative flex gap-3.5 pb-5 last:pb-0 md:block md:rounded-[22px] md:bg-[rgba(17,14,57,0.055)] md:p-5">
              {i < STEPS.length - 1 && (
                <span aria-hidden className="absolute start-4 top-9 h-[calc(100%-36px)] w-px md:hidden" style={{ background: HAIRLINE }} />
              )}
              <span
                aria-hidden
                className="relative grid h-8 w-8 shrink-0 place-items-center rounded-full font-sans text-[13px] font-bold"
                style={{ background: NAVY_GRADIENT, color: GOLD }}
              >
                {formatNumber(i + 1, lang)}
              </span>
              <div className="min-w-0 pt-1 md:pt-0">
                <h3 className="font-sans text-[15px] font-semibold md:mt-3" style={{ color: NAVY }}>
                  {t(`ambassadors.join.step_${s}_title`)}
                </h3>
                <p className="mt-0.5 font-sans text-[13.5px] leading-relaxed md:mt-1" style={{ color: MUTED }}>
                  {t(`ambassadors.join.step_${s}_body`)}
                </p>
              </div>
            </li>
          ))}
        </ol>

        <div className="mt-12 grid gap-4 md:grid-cols-3">
          {FAQS.map((f) => (
            <div key={f} className="rounded-[20px] p-5" style={{ boxShadow: `0 0 0 1px ${CONTROL_EDGE}` }}>
              <h3 className="font-sans text-[15px] font-semibold" style={{ color: NAVY }}>
                {t(`ambassadors.join.faq_${f}_q`)}
              </h3>
              <p className="mt-1.5 font-sans text-[14px] leading-relaxed" style={{ color: MUTED }}>
                {t(`ambassadors.join.faq_${f}_a`)}
              </p>
            </div>
          ))}
        </div>
      </section>

      {/* 4 · The application. */}
      <section
        id="apply"
        ref={applyRef}
        className="scroll-mt-24 py-16"
        style={{ background: "linear-gradient(180deg,#fff 0%,#f4f3f9 100%)" }}
        aria-labelledby="amb-apply-title"
      >
        <div className="mx-auto max-w-[640px] px-5">
          <div className="flex flex-col items-center text-center">
            <AmbassadorLogo width={200} />
            <h2
              id="amb-apply-title"
              className="mt-3 font-sans text-[26px] font-semibold sm:text-[32px]"
              style={{ color: NAVY, letterSpacing: "-0.03em" }}
            >
              {t("ambassadors.join.apply_title")}
            </h2>
            <p className="mt-2 font-sans text-[15px]" style={{ color: MUTED }}>
              {t("ambassadors.join.apply_subtitle")}
            </p>
          </div>
          <Panel className="mt-8 p-6" radius={28}>
            {status === "ambassador" ? (
              <StatusBlock
                icon="ri-shield-check-line"
                title={t("ambassadors.join.already_title")}
                body={t("ambassadors.join.already_body")}
                action={
                  <Link to="/account/ambassador" className={primaryClass("lg")} style={primaryStyle}>
                    {t("ambassadors.join.open_dashboard")}
                    <Icon name={forward} aria-hidden />
                  </Link>
                }
              />
            ) : sent || status === "pending" ? (
              <StatusBlock
                icon="ri-check-line"
                title={sent ? t("ambassadors.join.submitted_title") : t("ambassadors.join.pending_title")}
                body={t("ambassadors.join.pending_body")}
                action={
                  <>
                    {/* What happens next, in the words of How it works (suggestion 91). */}
                    <ol className="mb-5 flex flex-col gap-2.5 text-start" data-testid="sent-next">
                      {[t("ambassadors.join.sent_step_received"), t("ambassadors.join.step_approved_body"), t("ambassadors.join.sent_step_next")].map((line, k) => (
                        <li key={line} className="flex items-start gap-2.5 font-sans text-[14px]" style={{ color: NAVY }}>
                          <span
                            aria-hidden
                            className="grid h-6 w-6 shrink-0 place-items-center rounded-full text-[11.5px] font-bold"
                            style={k === 0 ? { background: NAVY_GRADIENT, color: GOLD } : { boxShadow: `0 0 0 1px ${CONTROL_EDGE}`, color: NAVY }}
                          >
                            {k === 0 ? <Icon name="ri-check-line" className="text-[13px]" /> : formatNumber(k + 1, lang)}
                          </span>
                          <span className="min-w-0 pt-0.5">{line}</span>
                        </li>
                      ))}
                    </ol>
                    <a
                      href={WHATSAPP_URL}
                      target="_blank"
                      rel="noopener noreferrer"
                      className={primaryClass("lg")}
                      style={primaryStyle}
                    >
                      <Icon name="ri-whatsapp-line" aria-hidden />
                      {t("booking.message_whatsapp")}
                    </a>
                    <p className="mt-2.5 font-sans text-[12.5px]" style={{ color: MUTED }} dir="ltr">
                      {SUPPORT_PHONE_DISPLAY}
                    </p>
                  </>
                }
              />
            ) : (
              <>
              {!session?.user && (
                <div className="mb-5 flex items-center gap-3 rounded-[18px] p-3.5" style={{ background: SOFT_TINT }} data-testid="sign-in-note">
                  <Icon name="ri-lock-line" className="shrink-0 text-[18px]" style={{ color: NAVY }} aria-hidden />
                  <div className="min-w-0 flex-1">
                    <p className="font-sans text-[14px] font-semibold" style={{ color: NAVY }}>
                      {t("ambassadors.join.sign_in_title")}
                    </p>
                    <p className="font-sans text-[12.5px]" style={{ color: MUTED }}>
                      {t("ambassadors.join.sign_in_body")}
                    </p>
                  </div>
                  <button type="button" onClick={goSignIn} className={secondaryClass("md")} style={secondaryStyle}>
                    {t("ambassadors.join.sign_in_cta")}
                  </button>
                </div>
              )}
              <ApplyForm
                signedIn={Boolean(session?.user)}
                defaultEmail={session?.user?.email ?? undefined}
                defaultName={session?.user?.name ?? undefined}
                carry={carry}
                onPlatformsChange={setFormPlatforms}
                onSubmit={async (input) => {
                  await apply.mutateAsync(input);
                }}
                onNeedSignIn={goSignIn}
                onSent={() => setSent(true)}
              />
              </>
            )}
          </Panel>
        </div>
      </section>

      <StickyApply heroRef={heroPillRef} formRef={applyRef} onApply={goToForm} hidden={status !== null || sent} />
    </div>
  );
}

function StatusBlock({
  icon,
  title,
  body,
  action,
}: {
  icon: string;
  title: string;
  body: string;
  action?: React.ReactNode;
}) {
  return (
    <div className="mj-rise-in flex flex-col items-center py-6 text-center">
      <span
        aria-hidden
        className="mj-pop-in grid h-14 w-14 place-items-center rounded-full"
        style={{ background: NAVY_GRADIENT, color: GOLD }}
      >
        <Icon name={icon} className="text-[26px]" />
      </span>
      <h3 className="mt-4 font-sans text-[20px] font-semibold" style={{ color: NAVY }}>
        {title}
      </h3>
      <p className="mt-1 max-w-[380px] font-sans text-[14.5px]" style={{ color: MUTED, lineHeight: 1.55 }}>
        {body}
      </p>
      {action ? <div className="mt-6 flex w-full max-w-[360px] flex-col items-stretch">{action}</div> : null}
    </div>
  );
}
